import { ApolloQueryResult } from '@apollo/client';
import apolloClient from 'api/apolloClient';
import {
  GENERATE_BENEFIT_SUMMARIES,
  GENERATE_EXHIBITS,
  GENERATE_PROPOSAL,
  GET_INCLUDED_BENEFITS,
  GET_INITIAL_SEED_BENEFIT_ZIP,
  GET_PROPOSAL_DOWNLOAD_URL,
  GET_ESTIMATE_COST_ANSWERS,
  GET_PROPOSAL_STATE,
  GET_PROPOSAL_STATUS,
  SAVE_ESTIMATE_COST_ANSWERS,
  GET_PROPOSAL_UPLOAD_URL,
  RESET_BENEFIT_SUMMARIES,
  SAVE_GENERATE_PROPOSAL_ANSWERS,
  SHARE_PROPOSAL,
} from 'api/queries/proposalServiceV3';
import { FetchResult } from 'apollo-link';
import { Primitive } from 'types/general';
import { InputGenerateProductTemplate } from 'types/productServiceV2';
import {
  ProposalFileType,
  ProposalGenerationState,
  ProposalGenerationStatus,
  ProposalRequest,
} from 'types/proposalService';

export async function generateBenefitSummaries(
  sponsorId: string,
  productTemplate: InputGenerateProductTemplate,
  includedBenefits: Record<string, boolean>,
  catalogId: string,
): Promise<FetchResult<{ generateBenefitSummaries: string }>> {
  return apolloClient.mutate<{ generateBenefitSummaries: string }>({
    mutation: GENERATE_BENEFIT_SUMMARIES,
    fetchPolicy: 'no-cache',
    variables: { sponsorId, productTemplate, includedBenefits, catalogId },
  });
}

export async function resetBenefitSummaries(
  templateId: string,
): Promise<FetchResult<{ resetBenefitSummaries: boolean }>> {
  return apolloClient.mutate<{ resetBenefitSummaries: boolean }>({
    mutation: RESET_BENEFIT_SUMMARIES,
    fetchPolicy: 'no-cache',
    variables: { templateId },
  });
}

export async function getIncludedBenefits(
  templateId: string,
): Promise<ApolloQueryResult<{ getIncludedBenefits: Record<string, boolean> | null }>> {
  return apolloClient.query<{ getIncludedBenefits: Record<string, boolean> | null }>({
    query: GET_INCLUDED_BENEFITS,
    fetchPolicy: 'no-cache',
    variables: { templateId },
  });
}

export async function getProposalGenerationState(cartId: string): Promise<ProposalGenerationState> {
  return (
    await apolloClient.query<{ getProposalState: ProposalGenerationState }>({
      query: GET_PROPOSAL_STATE,
      fetchPolicy: 'no-cache',
      variables: { cartId },
    })
  ).data.getProposalState;
}

export async function getEstimateCostAnswers(cartId: string): Promise<Record<string, string>> {
  const answers = (
    await apolloClient.query<{ getProposalState: { estimateCostAnswers: Record<string, Primitive> | null } | null }>({
      query: GET_ESTIMATE_COST_ANSWERS,
      fetchPolicy: 'no-cache',
      variables: { cartId },
    })
  ).data.getProposalState?.estimateCostAnswers;

  if (!answers) return {};

  return Object.fromEntries(
    Object.entries(answers)
      .filter((entry): entry is [string, Exclude<Primitive, null | undefined>] => entry[1] != null)
      .map(([key, value]) => [key, String(value)]),
  );
}

export async function saveEstimateCostAnswers(
  cartId: string,
  answers: Record<string, Primitive | string[]>,
): Promise<Record<string, Primitive | string[]>> {
  return (
    await apolloClient.mutate<{ saveEstimateCostAnswers: Record<string, Primitive | string[]> }>({
      mutation: SAVE_ESTIMATE_COST_ANSWERS,
      fetchPolicy: 'no-cache',
      variables: { cartId, answers },
    })
  ).data!.saveEstimateCostAnswers;
}

export async function getProposalGenerationStatus(
  cartId: string,
  lastUpdateTime: string,
): Promise<ProposalGenerationStatus> {
  return (
    await apolloClient.query<{ getProposalStatus: ProposalGenerationStatus }>({
      query: GET_PROPOSAL_STATUS,
      fetchPolicy: 'no-cache',
      variables: { cartId, lastUpdateTime },
    })
  ).data.getProposalStatus;
}

export async function saveGenerateProposalAnswers(
  cartId: string,
  answers: Record<string, Primitive | string[]>,
): Promise<Record<string, string>> {
  return (
    await apolloClient.mutate<{ generateProposalAnswers: Record<string, string> }>({
      mutation: SAVE_GENERATE_PROPOSAL_ANSWERS,
      fetchPolicy: 'no-cache',
      variables: { cartId, answers },
    })
  ).data!.generateProposalAnswers;
}

export async function getProposalDownloadURL(cartId: string, fileType: ProposalFileType): Promise<string> {
  return (
    await apolloClient.query<{ getProposalDownloadURL: string }>({
      query: GET_PROPOSAL_DOWNLOAD_URL,
      fetchPolicy: 'no-cache',
      variables: { cartId, fileType },
    })
  ).data.getProposalDownloadURL;
}

export async function generateProposal(request: ProposalRequest, confirmedOnly = false): Promise<boolean> {
  return (
    await apolloClient.mutate<{ generateProposal: boolean }>({
      mutation: GENERATE_PROPOSAL,
      fetchPolicy: 'no-cache',
      variables: { request, confirmedOnly },
    })
  ).data!.generateProposal;
}

export async function shareProposal(
  cartId: string,
  groupId: string,
  cartName: string,
  primaryEmail: string,
  cc: string[],
  message?: string,
): Promise<boolean> {
  return (
    await apolloClient.query<boolean>({
      query: SHARE_PROPOSAL,
      fetchPolicy: 'no-cache',
      variables: { cartId, groupId, primaryEmail, cc, message, cartName },
    })
  ).data;
}

export async function getInitialSeedBenefitZip(sponsorId: string): Promise<string> {
  return (
    await apolloClient.mutate<{ getInitialSeedBenefitsZip: string }>({
      mutation: GET_INITIAL_SEED_BENEFIT_ZIP,
      fetchPolicy: 'no-cache',
      variables: { sponsorId },
    })
  ).data!.getInitialSeedBenefitsZip;
}

export async function getProposalUploadURL(request: ProposalRequest, fileName: string): Promise<string> {
  return (
    await apolloClient.mutate<{ getProposalUploadURL: string }>({
      mutation: GET_PROPOSAL_UPLOAD_URL,
      fetchPolicy: 'no-cache',
      variables: { request, fileName },
    })
  ).data!.getProposalUploadURL;
}

export async function generateExhibits(sponsorId: string, effectiveDate: string): Promise<boolean> {
  return (
    await apolloClient.mutate<{ generateExhibits: boolean }>({
      mutation: GENERATE_EXHIBITS,
      fetchPolicy: 'no-cache',
      variables: { sponsorId, effectiveDate },
    })
  ).data!.generateExhibits;
}
